import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useState } from "react";
import type { FC } from "react";
import styled from "styled-components";

import WeekTodoSortableItem from "./WeekTodoSortableItem";

interface Props {
  orderedKeys: string[];
  getLabel: (key: string) => string;
  onChange: (newOrder: string[]) => void;
}

/**
 * 주간 숙제 정렬 모드 UI.
 * - 각 항목을 라벨 + 드래그 핸들로만 간단 표시 (체크/카운터 인터랙션은 잠시 숨김)
 * - 드래그 재배치 → onChange로 새 순서 전달 (저장은 부모가 수행)
 */
const WeekTodoSortWrap: FC<Props> = ({ orderedKeys, getLabel, onChange }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      setActiveId(null);
      return;
    }
    if (active.id !== over.id) {
      const oldIndex = orderedKeys.indexOf(active.id as string);
      const newIndex = orderedKeys.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) {
        setActiveId(null);
        return;
      }
      onChange(arrayMove(orderedKeys, oldIndex, newIndex));
    }
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
        <Container>
          {orderedKeys.map((key) => (
            <WeekTodoSortableItem key={key} id={key} label={getLabel(key)} />
          ))}
        </Container>
      </SortableContext>
      <DragOverlay>
        {activeId ? (
          <OverlayRow>
            <OverlayLabel>{getLabel(activeId)}</OverlayLabel>
          </OverlayRow>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default WeekTodoSortWrap;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 6px;
  overflow: hidden;
`;

const OverlayRow = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.app.bg.gray1};
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const OverlayLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.dark2};
`;
